import { logger } from '@/lib/logger';
import React, { useEffect, useState, Suspense, lazy, createContext, startTransition } from 'react';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as HotToaster } from 'react-hot-toast';
import { DomainProvider, useDomain } from '@/lib/domain-context';
import { NavbarProvider } from '@/contexts/NavbarContext';
import { CloseProvider } from '@/contexts/close-context';
import { PaywallProvider } from '@/contexts/paywall-context';
import { LayoutDebugger } from '@/components/LayoutDebugger';
import { QueryClientProvider } from '@tanstack/react-query';
import { useFlag, useFlagsStatus } from '@unleash/proxy-client-react';
import { unleashEnabled } from '@/lib/feature-flags';
import { queryClient } from '@/lib/query-client';
import { ThemeProvider } from '@/lib/theme-provider';
import { SubscriptionProvider } from '@/contexts/SubscriptionContext';
import { LearningProfileProvider, OnboardingModalHost } from '@/contexts/LearningProfileContext';
import { logEnvironmentInfo } from '@/lib/environment';
import { useAnalytics } from '@/hooks/use-analytics';
import { useUTMTracking } from '@/hooks/use-utm-tracking';


// Log environment information on app startup
if (import.meta.env.DEV) {
  logEnvironmentInfo();
}

// Direct imports for homepage components
import { HomePage } from '@/components/HomePage';

// Lazy-load legal pages (rarely accessed, don't need in main bundle)
const TermsOfService = lazy(() => import('@/pages/TermsOfService').then(module => ({ default: module.TermsOfService })));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy').then(module => ({ default: module.PrivacyPolicy })));
const Disclaimer = lazy(() => import('@/pages/Disclaimer').then(module => ({ default: module.Disclaimer })));

// Lazy-load password reset pages
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage').then(module => ({ default: module.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage').then(module => ({ default: module.ResetPasswordPage })));

// Lazy-load PersistentLayout — only mounted on authenticated routes.
// Eagerly importing it pulled the entire sidebar/navbar/dashboard tree into
// the entry bundle, hurting homepage LCP. Routes that use it as a layout
// element still work — React Router suspends on the layout's render until
// the chunk loads, then renders the matched <Outlet />.
const PersistentLayout = lazy(() => import('@/components/layout/PersistentLayout').then(module => ({ default: module.PersistentLayout })));

// FlagGate — Unleash flag-gated render wrapper. Use for route-level gating
// (F.10 wrap). Layered ON TOP of existing ProtectedRoute / NextGenContentGate
// / entitlement checks — those stay intact.
import { FlagGate } from '@/components/guards/FlagGate';

// Import ScrollToTop for universal route changes
import ScrollToTop from '@/components/flashcards/ScrollToTop';

// Lazy load large components
// Note: The React.StrictMode in main.tsx causes components to mount twice in development
// This is normal and helps catch side effect bugs. It does NOT indicate duplicate components in production.
// IMPORTANT: AuthProvider is already present at the app root level. DO NOT add additional AuthProviders in route components.
const AuthPage = lazy(() => import('@/components/auth/AuthPage').then(module => ({ default: module.AuthPage })));
const FlashcardsPage = lazy(() => import('@/components/flashcards/FlashcardsPageWrapper'));
const CoursesPage = lazy(() => import('@/components/courses/CoursesPageWrapper'));
// Lazy load admin components to keep them out of main bundle
const LazyAdminCourseDetail = lazy(() => import('./components/admin/CourseDetail').then(m => ({ default: m.CourseDetail })));
const PublicCourseDetail = lazy(() => import('@/components/courses/CourseDetail'));
const SubscriptionSuccess = lazy(() => import('@/components/SubscriptionSuccess').then(module => ({ default: module.SubscriptionSuccess })));

// Lazy-load ChatContainer (only used on /chat route)
const ChatContainer = lazy(() => import('@/components/chat/ChatContainer'));

// Lazy-load Dashboards — authenticated-only routes, no reason to ship in entry bundle.
// Pulled out of main bundle 2026-05-25 (board 8iK8ZuVwbtHaa9Pvrtvu) — was wasting
// ~150KB+ of homepage payload. All callers already wrap in <Suspense>.
const JDSDashboard = lazy(() => import('@/pages/Dashboard'));
const IntelligenceDashboard = lazy(() => import('@/pages/IntelligenceDashboard'));

// Lazy load diagnostic page
const DiagnosticPage = lazy(() => import('@/pages/diagnostic/DiagnosticPage'));

// Note: Syllabus page moved to /dashboard/classes (ClassesTabContent)

// Lazy load study plan page
const StudyPlanPage = lazy(() => import('@/pages/study-plan/StudyPlanPage'));
const StudyPlanRedirect = lazy(() => import('@/pages/study-plan/StudyPlanRedirect'));

// Lazy load score import page
const ImportScoresPage = lazy(() => import('@/pages/score-import/ImportScoresPage'));

// Import directly for debugging purposes
// import JDSDashboardDirect from '@/pages/Dashboard';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { OfflineIndicator } from '@/components/OfflineIndicator';
import { UpdateBanner } from '@/components/UpdateBanner';
import {
  Routes,
  Route,
  Navigate,
  BrowserRouter,
  useParams,
  useLocation,
  useNavigate,
  Outlet,
} from 'react-router-dom';
import { initializeDeepLinks } from '@/lib/capacitor/deep-links';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { NativePurchaseGuard } from '@/components/native';

// Forces NextGenMCQPracticePage to remount on every navigation so drill state
// (drillAutoStarted ref, infiniteSession, pageState) resets cleanly.
const MCQPracticeWithReset = () => {
  const { key } = useLocation();
  return <NextGenMCQPracticePage key={key} />;
};
import { CourseRedirect } from './components/courses/CourseRedirect';

// Lazy-load SettingsPage — authenticated-only, kept out of entry bundle.
// Pulled out of main bundle 2026-05-25 (board 8iK8ZuVwbtHaa9Pvrtvu).
const SettingsPage = lazy(() => import('@/components/settings/SettingsPage').then(module => ({ default: module.SettingsPage })));
// Import trial banner for global display
import { GlobalTrialBanner } from '@/components/subscription/TrialBanner';
import { TrialBannerProvider } from '@/contexts/TrialBannerContext';
// Import our admin components
const AdminDashboard = lazy(() => import('@/components/admin/Dashboard').then(module => ({ default: module.default })));
const AdminUsers = lazy(() => import('@/components/admin/Users').then(module => ({ default: module.default })));
const AdminUserDetailPage = lazy(() => import('@/pages/Admin/AdminUserDetailPage').then(module => ({ default: module.default })));
const AdminWebAnalytics = lazy(() => import('@/components/admin/WebAnalytics').then(module => ({ default: module.default })));
const AdminErrorLogs = lazy(() => import('@/components/admin/ErrorLogs').then(module => ({ default: module.default })));
// JDS Features admin host — consolidates Courses overview / Flashcards / Outlines /
// Case Law behind one segmented-control page. Each CRUD tab lazy-mounts inside it,
// so AdminFlashcards/AdminOutlines/AdminCases are imported there, not here.
const JdsFeaturesPage = lazy(() => import('@/pages/Admin/JdsFeaturesPage').then(module => ({ default: module.JdsFeaturesPage })));
const AdminBlogManagement = lazy(() => import('@/components/admin/blog/BlogManagement').then(module => ({ default: module.BlogManagement })));
const BlogAIGenerator = lazy(() => import('@/pages/Admin/BlogAIGenerator').then(module => ({ default: module.BlogAIGenerator })));
const AdminSettings = lazy(() => import('@/components/admin/Settings').then(module => ({ default: module.default })));
const AdminContentHealth = lazy(() => import('@/components/admin/ContentHealth').then(module => ({ default: module.default })));
const AdminCommunityModeration = lazy(() => import('@/components/admin/CommunityModeration').then(module => ({ default: module.default })));
const AdminSupport = lazy(() => import('@/components/admin/AdminSupport').then(module => ({ default: module.default })));
const SetAdminStatus = lazy(() => import('@/components/admin/SetAdmin').then(module => ({ default: module.default })));
const NextGenSalesPage = lazy(() => import('@/pages/Admin/NextGenSalesPage').then(m => ({ default: m.default })));

// Import debug components
const UsermavenDebug = lazy(() => import('@/components/debug/UsermavenDebug').then(module => ({ default: module.UsermavenDebug })));

// Import outline pages
const OutlineLibrary = lazy(() => import('@/pages/outlines/OutlineLibrary'));
const OutlineAnalyses = lazy(() => import('@/pages/outlines/OutlineAnalysesWrapper'));
const UploadOutlinePage = lazy(() => import('@/pages/outlines/UploadOutlinePage').then(module => ({ default: module.UploadOutlinePage })));
const OutlineAnalysisDetail = lazy(() => import('@/pages/outlines/OutlineAnalysisDetail').then(module => ({ default: module.OutlineAnalysisDetail })));
const OutlineArchive = lazy(() => import('@/pages/outlines/OutlineArchive'));

// Import NextGen bar prep pages
const NextGenLandingPage = lazy(() => import('@/pages/nextgen/NextGenLandingPage').then(module => ({ default: module.NextGenLandingPage })));
const NextGenUpgradePage = lazy(() => import('@/pages/nextgen/NextGenUpgradePage').then(module => ({ default: module.NextGenUpgradePage })));
const NextGenPrepPage = lazy(() => import('@/pages/nextgen/NextGenPrepPage').then(module => ({ default: module.NextGenPrepPage })));
const NextGenPrepPreviewPage = lazy(() => import('@/pages/nextgen/NextGenPrepPreviewPage').then(module => ({ default: module.NextGenPrepPreviewPage })));
const NextGenPreviewPage = lazy(() => import('@/pages/nextgen/NextGenPreviewPage').then(module => ({ default: module.NextGenPreviewPage })));
const NextGenTaskDetail = lazy(() => import('@/pages/nextgen/NextGenTaskDetail').then(module => ({ default: module.NextGenTaskDetail })));
const NextGenActivatePage = lazy(() => import('@/pages/nextgen/NextGenActivatePage').then(module => ({ default: module.NextGenActivatePage })));
const NextGenPurchaseConfirmedPage = lazy(() => import('@/pages/nextgen/NextGenPurchaseConfirmedPage').then(module => ({ default: module.NextGenPurchaseConfirmedPage })));
// Workspace pages retired (2026-04-11) — replaced by NCBE-aligned practice system.
// NextGenWorkbenchPage / NextGenHeatmapPage / NextGenGradeReport deleted from disk 2026-05-04.
// Heatmap activity type fully removed 2026-05-25 (components, hook, DB tables, task rows).
// client_chat task type retired 2026-05-25 (board RYu91USgyqQZ3W7gj4qi). NextGenClientChatPage
// + useNextGenCounseling deleted from disk 2026-05-26 (zero rows in nextgen_tasks, no
// remaining consumers). Old /nextgen/client-chat/:taskId and /nextgen/practice/counseling/:taskId
// still redirect to /nextgen/practice/pt for backward compat with any stale bookmarks.
const NextGenDashboardPage = lazy(() => import('@/pages/nextgen/NextGenDashboardPage').then(module => ({ default: module.NextGenDashboardPage })));
const NextGenSkillsPage = lazy(() => import('@/pages/nextgen/NextGenSkillsPage').then(module => ({ default: module.NextGenSkillsPage })));
const NextGenProgressPage = lazy(() => import('@/pages/nextgen/NextGenProgressPage').then(module => ({ default: module.NextGenProgressPage })));
// Lazy-load CourseLibrary/CoursePlayer — only used inside NextGen coursework routes.
// Pulled out of main bundle 2026-05-25 (board 8iK8ZuVwbtHaa9Pvrtvu).
const CourseLibrary = lazy(() => import('@/components/courses/CourseLibrary').then(module => ({ default: module.CourseLibrary })));
const CoursePlayer = lazy(() => import('@/components/courses/CoursePlayer').then(module => ({ default: module.CoursePlayer })));
const NextGenSimulationsPage = lazy(() => import('@/pages/nextgen/NextGenSimulationsPage').then(module => ({ default: module.NextGenSimulationsPage })));
const NextGenFullExamPage = lazy(() => import('@/pages/nextgen/NextGenFullExamPage').then(module => ({ default: module.NextGenFullExamPage })));
const NextGenExamResultsPage = lazy(() => import('@/pages/nextgen/NextGenExamResultsPage').then(module => ({ default: module.NextGenExamResultsPage })));
const NextGenCoachPage = lazy(() => import('@/pages/nextgen/NextGenCoachPage'));
const NextGenExamMapPage = lazy(() => import('@/pages/nextgen/NextGenExamMapPage').then(module => ({ default: module.NextGenExamMapPage })));
const NextGenMCQPracticePage = lazy(() => import('@/pages/nextgen/NextGenMCQPracticePage').then(module => ({ default: module.NextGenMCQPracticePage })));
const NextGenIQSPracticePage = lazy(() => import('@/pages/nextgen/NextGenIQSPracticePage').then(module => ({ default: module.NextGenIQSPracticePage })));
const NextGenPTPracticePage = lazy(() => import('@/pages/nextgen/NextGenPTPracticePage').then(module => ({ default: module.NextGenPTPracticePage })));
const NextGenDraftingPracticePage = lazy(() => import('@/pages/nextgen/NextGenDraftingPracticePage').then(module => ({ default: module.NextGenDraftingPracticePage })));
const NextGenSectionSimPage = lazy(() => import('@/pages/nextgen/NextGenSectionSimPage').then(module => ({ default: module.NextGenSectionSimPage })));
const NextGenSimReviewPage = lazy(() => import('@/pages/nextgen/NextGenSimReviewPage').then(module => ({ default: module.NextGenSimReviewPage })));
const NextGenMCQSinglePage = lazy(() => import('@/pages/nextgen/NextGenMCQSinglePage').then(module => ({ default: module.NextGenMCQSinglePage })));
const NextGenSetRedirect = lazy(() => import('@/pages/nextgen/NextGenSetRedirect').then(module => ({ default: module.NextGenSetRedirect })));
const NextGenLayout = lazy(() => import('@/components/nextgen/NextGenLayout').then(module => ({ default: module.NextGenLayout })));
// Lazy-load NextGenContentGate — only used inside NextGen routes.
// Pulled out of main bundle 2026-05-25 (board 8iK8ZuVwbtHaa9Pvrtvu).
const NextGenContentGate = lazy(() => import('@/components/nextgen/NextGenContentGate').then(module => ({ default: module.NextGenContentGate })));

// UBE/MBE standalone bar-prep product (board task chxw5cJrcarpNLY2HrDg).
// UbeLayout self-gates on the `ube-bar-prep` flag (FlagGate → Navigate to "/").
const UbeLayout = lazy(() => import('@/components/ube/UbeLayout').then(module => ({ default: module.UbeLayout })));
const UbeProgressPage = lazy(() => import('@/pages/ube/UbeProgressPage').then(module => ({ default: module.UbeProgressPage })));
const UbeComingSoonPage = lazy(() => import('@/pages/ube/UbeComingSoonPage').then(module => ({ default: module.UbeComingSoonPage })));
// /ube/coach — MEE essay practice-prompt picker host (board Q016kyzpVShp47yrCrR7).
// Browse surface: visible to all flag-on users; the submit path is the entitlement
// leaf (free-quota + UbeContentGate, board 0UUgu5hQ), not a hard gate here.
const UbeCoachPage = lazy(() => import('@/pages/ube/UbeCoachPage').then(module => ({ default: module.UbeCoachPage })));
// UBE browse-mode paywall (board 87q2TNhDml6gO9d2BTp8). The four functional
// /ube/* pages are browseable by everyone (flag-gated only) — unentitled users
// see the analytics with a <UbeBrowseModeCallout> nudge, not a redirect.
// UbeContentGate (src/components/ube/UbeContentGate.tsx) is intentionally NOT
// imported here: it is the hard-redirect gate reserved for FUTURE Legacy
// interaction-leaf routes (take a question beyond free quota, run a full sim,
// deep coach), mirroring how NextGenContentGate wraps only act-on-content
// leaves. UbePricingPage is the public sales page the callout CTA links to.
const UbePricingPage = lazy(() => import('@/pages/ube/UbePricingPage').then(module => ({ default: module.UbePricingPage })));

// Import case brief pages
const CaseBriefPage = lazy(() => import('@/pages/case-briefs/CaseBriefPage'));



// Import Quiz Mode pages (SHELVED - not working well, may be restored later)
// const QuizSetup = lazy(() => import('@/pages/quiz/QuizSetup'));
// const QuizMode = lazy(() => import('@/pages/quiz/QuizMode'));

// Issue Spotter and Essay Coach - enabled for dev, disabled in production
const IssueSpotterPageWrapper = lazy(() => import('@/components/issue-spotter/IssueSpotterPageWrapper'));
const EssayCoachPageWrapper = lazy(() => import('@/components/essay-coach/EssayCoachPageWrapper'));

// Practice landing page (gateway to Issue Spotter + Essay Coach)
const PracticePage = lazy(() => import('@/pages/PracticePage'));

// Import notes page
const NotesPage = lazy(() => import('@/pages/NotesPageWrapper'));

// Import community page
const CommunityPage = lazy(() => import('@/components/community/CommunityPageWrapper'));
const GroupInviteLanding = lazy(() => import('@/components/community/GroupInviteLanding').then(m => ({ default: m.GroupInviteLanding })));
const StudyTogetherPage = lazy(() => import('@/pages/community/StudyTogetherPage').then(m => ({ default: m.StudyTogetherPage })));

// Flag-gated wrapper for /community/groups/join/:code — the invite landing is a
// study-group entry point, so it needs BOTH community-board (umbrella) AND
// community-study-groups (sub-feature) to be ON.
//
// MUST wait for flagsReady before deciding: useFlag returns false while the
// Unleash client is still fetching, and this route is an external entry point
// hit by FIRST-TIME visitors (invite recipients) with no localStorage flag
// cache — redirecting on the initial false reading sent every invitee to
// /dashboard before the flags arrived. Returning users never saw it because
// their cached flags resolve true instantly.
function FlagGatedGroupInvite() {
  const boardEnabled = useFlag('community-board');
  const studyGroupsEnabled = useFlag('community-study-groups');
  const { flagsReady, flagsError } = useFlagsStatus();

  // SDK still fetching (and not errored) — hold with a spinner, don't decide yet.
  if (unleashEnabled && !flagsReady && !flagsError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="h-8 w-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!boardEnabled || !studyGroupsEnabled) {
    return <Navigate to="/dashboard" replace />;
  }
  return <GroupInviteLanding />;
}

// Import cases pages
const CasesSearchPage = lazy(() => import('@/pages/cases/CasesSearchPage'));
const CaseDetailPage = lazy(() => import('@/pages/cases/CaseDetailPage'));

// Lazy load SetAdminSetup to avoid pulling admin bundle into main app
const SetAdminSetup = lazy(() => import('./components/admin/SetAdmin').then(module => ({ default: module.default })));

// Import the checkout confirmation page
const CheckoutConfirmationPage = lazy(() => import('@/pages/CheckoutConfirmationPage').then(module => ({ default: module.CheckoutConfirmationPage })));

// Public cancellation-feedback survey — token-authenticated, NO auth required
// (the ?token= in the email link is the credential). Standalone one-page form,
// registered OUTSIDE ProtectedRoute / PersistentLayout. Board ODItTB29ohrpwAgU2gUf.
const CancelFeedbackPage = lazy(() => import('@/pages/CancelFeedbackPage'));

// Import the pricing page
const PricingPage = lazy(() => import('@/pages/PricingPage').then(module => ({ default: module.PricingPage })));

// Import feature landing pages
const OutlinesFeaturePage = lazy(() => import('@/pages/features/OutlinesFeaturePage'));
const SubjectPreviewPage = lazy(() => import('@/pages/outlines/SubjectPreviewPage'));

// Import AuthCallback
import AuthCallback from './pages/auth/AuthCallback';

// Import Roadmap page
const Roadmap = lazy(() => import('@/pages/Roadmap').then(module => ({ default: module.Roadmap })));
import ResendConfirmationPage from './pages/auth/ResendConfirmationPage';

// Import NotFoundPage for 404 handling
const NotFoundPage = lazy(() => import('@/pages/common/NotFoundPage'));

// Check if admin setup is allowed from environment variables
const allowSetupAdmin = import.meta.env.VITE_ALLOW_ADMIN_SETUP === 'true';

// Import sidebar context from separate file to avoid circular dependencies
import { SidebarContext } from '@/contexts/SidebarContext';

// Create thread context
export type SelectedThreadContextType = {
  selectedThreadId: string | null;
  setSelectedThreadId: (threadId: string | null) => void;
};

export const SelectedThreadContext = createContext<SelectedThreadContextType>({
  selectedThreadId: null,
  setSelectedThreadId: () => {}
});

// Add a simple redirect component that doesn't trigger authentication initialization
const SimpleRedirect = ({ to }: { to: string }) => {
  return <Navigate to={to} replace />;
};

// Redirect that preserves :taskId param from old /nextgen/practice/:taskId URLs
const PracticeTaskRedirect = () => {
  const { taskId } = useParams();
  return <Navigate to={`/nextgen/skills/${taskId}`} replace />;
};

// Thin wrappers for NextGen coursework using shared components
const NextGenCourseworkLibrary = () => (
  <div className="max-w-6xl mx-auto px-4 pt-6 pb-6">
    <CourseLibrary courseType="nextgen" basePath="/nextgen/coursework" />
  </div>
);

const NextGenCourseworkPlayer = () => (
  <CoursePlayer basePath="/nextgen/coursework" courseType="nextgen" />
);


// Create a generic async wrapper for lazy-loaded components
const createAsyncWrapper = (Component: React.ComponentType, fallbackMessage: string = "Loading...") => {
  const AsyncWrapper = function AsyncWrapper() {
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
      // Use a small delay to ensure the route transition completes first
      const timeoutId = setTimeout(() => {
        startTransition(() => {
          setIsReady(true);
        });
      }, 50);

      return () => clearTimeout(timeoutId);
    }, []);

    if (!isReady) {
      return (
        <div className="flex justify-center items-center min-h-screen bg-white dark:bg-gray-900">
          <div className="flex flex-col items-center">
            <LoadingSpinner className="w-8 h-8" />
            <p className="mt-4 text-gray-500 dark:text-gray-400">{fallbackMessage}</p>
          </div>
        </div>
      );
    }

    return (
      <Suspense fallback={
        <div className="flex justify-center items-center min-h-screen bg-white dark:bg-gray-900">
          <div className="flex flex-col items-center">
            <LoadingSpinner className="w-8 h-8" />
            <p className="mt-4 text-gray-500 dark:text-gray-400">{fallbackMessage}</p>
          </div>
        </div>
      }>
        <Component />
      </Suspense>
    );
  };
  AsyncWrapper.displayName = `AsyncWrapper(${Component.displayName || Component.name || 'Component'})`;
  return AsyncWrapper;
};


// Create async wrappers for components that may cause suspense issues
const AsyncAuthPage = createAsyncWrapper(AuthPage, "Loading authentication...");
const AsyncCheckoutConfirmationPage = createAsyncWrapper(CheckoutConfirmationPage, "Loading confirmation...");
const AsyncPricingPage = createAsyncWrapper(PricingPage, "Loading pricing...");

import { AdminGuardedRoute } from './components/guards/AdminGuardedRoute';
import { AdminOnlyRoute } from './components/guards/AdminOnlyRoute';
import { StudicataGuardedRoute } from './components/guards/StudicataGuardedRoute';

// Create router with domain-aware routes
function AppRoutes() {
  const { isJDSimplified, isAskJDS, isAdmin, isStudicata } = useDomain();

  logger.debug('Rendering routes', { isJDSimplified, isAdmin, isStudicata });

  // Studicata partner portal — NextGen only, no community, no homepage
  // Access restricted to is_admin=true OR role='partner' via StudicataGuardedRoute
  // Uses layout route pattern (like admin) to avoid React Error #426 from sync Navigate during route transitions
  if (isStudicata) {
    return (
      <>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Navigate to="/admin/nextgen-sales" replace />} />
          <Route path="/auth" element={<AsyncAuthPage />} />
          <Route path="/nextgen" element={<Navigate to="/nextgen/skills/practice" replace />} />

          {/* Studicata guarded routes — PersistentLayout provides sidebar */}
          <Route element={
            <StudicataGuardedRoute>
              <Suspense fallback={<PageLoader message="Loading..." />}>
                <PersistentLayout />
              </Suspense>
            </StudicataGuardedRoute>
          }>
          <Route path="/nextgen/dashboard" element={
            <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
              <NextGenLayout><NextGenDashboardPage /></NextGenLayout>
            </Suspense>
          } />
          <Route path="/nextgen/coursework" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenCourseworkLibrary /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/coursework/:courseId" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenCourseworkPlayer /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/coursework/:courseId/:moduleId" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenCourseworkPlayer /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/skills" element={<Navigate to="/nextgen/skills/practice" replace />} />
          <Route path="/nextgen/skills/practice" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenSkillsPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/skills/drills" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenSkillsPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/skills/library" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenSkillsPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/skills/mcq" element={<Navigate to="/nextgen/practice/mcq?formats=mcq,mcq_b" replace />} />
          <Route path="/nextgen/skills/iqs" element={<Navigate to="/nextgen/practice/iqs" replace />} />
          <Route path="/nextgen/skills/pt" element={<Navigate to="/nextgen/practice/pt" replace />} />
          <Route path="/nextgen/skills/:taskId" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenTaskDetail /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/simulations" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenSimulationsPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/sim/:sectionNumber" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenSectionSimPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/sim/:sessionId/review" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenSimReviewPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/exam/:runId" element={
            <Suspense fallback={<PageLoader />}><NextGenFullExamPage /></Suspense>
          } />
          <Route path="/nextgen/exam/:runId/results" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenExamResultsPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/progress" element={<Navigate to="/nextgen/progress/overview" replace />} />
          <Route path="/nextgen/progress/overview" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenProgressPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/progress/readiness" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenProgressPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/progress/insights" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenProgressPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/practice" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenMCQPracticePage /></NextGenLayout></Suspense>
          } />
          {/* Old workspace routes retired — redirect to practice system */}
          <Route path="/nextgen/workbench/:taskId" element={<Navigate to="/nextgen/practice/pt" replace />} />
          <Route path="/nextgen/client-chat/:taskId" element={<Navigate to="/nextgen/practice/pt" replace />} />
          <Route path="/nextgen/grade/:taskId" element={<Navigate to="/nextgen/practice/pt" replace />} />
          <Route path="/nextgen/coach" element={
            <Suspense fallback={<PageLoader />}><NextGenCoachPage /></Suspense>
          } />
          <Route path="/nextgen/exam-map" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenExamMapPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/practice/mcq" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><MCQPracticeWithReset /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/practice/mcq/:questionId" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenMCQSinglePage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/sets/:setId" element={
            <Suspense fallback={<PageLoader />}><NextGenSetRedirect /></Suspense>
          } />
          {/* Legacy redirect — keep for backward compat */}
          <Route path="/nextgen/mcq-practice" element={<Navigate to="/nextgen/practice/mcq" replace />} />
          <Route path="/nextgen/practice/iqs" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenIQSPracticePage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/practice/iqs/:setId" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenIQSPracticePage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/practice/pt" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenPTPracticePage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/practice/pt/:setId" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenPTPracticePage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/practice/drafting" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenDraftingPracticePage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/practice/drafting/:setId" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenDraftingPracticePage /></NextGenLayout></Suspense>
          } />
          {/* /nextgen/practice/counseling/:taskId route retired 2026-05-25 (board
              RYu91USgyqQZ3W7gj4qi) — client_chat task type has zero rows. */}
          <Route path="/nextgen/practice/counseling/:taskId" element={<Navigate to="/nextgen/practice/pt" replace />} />
          <Route path="/nextgen/coursework/:courseId/:moduleId/:lessonId" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenCourseworkPlayer /></NextGenLayout></Suspense>
          } />

          {/* Standard JDS user routes — so sidebar links work on studicata domain */}
          <Route path="/chat/:threadId?" element={
            <FlagGate flag="ask-jds" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading chat..." />}><ChatContainer /></Suspense>
            </FlagGate>
          } />
          <Route path="/settings/*" element={<SettingsPage />} />
          <Route path="/dashboard/*" element={
            <Suspense fallback={<PageLoader message="Loading dashboard..." />}>
              <NavbarProvider><IntelligenceDashboard /></NavbarProvider>
            </Suspense>
          } />
          <Route path="/flashcards/*" element={
            <FlagGate flag="flashcards" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading flashcards..." />}>
                <NavbarProvider><FlashcardsPage /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/outlines" element={<Navigate to="/outlines/library" replace />} />
          <Route path="/outlines/library" element={
            <FlagGate flag="outlines" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading outline library..." />}>
                <NavbarProvider><OutlineLibrary /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/outlines/analyses" element={
            <FlagGate flag="outline-iq" fallback={<Navigate to="/outlines/library" replace />}>
              <Suspense fallback={<PageLoader message="Loading your analyses..." />}>
                <NavbarProvider><OutlineAnalyses /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/outlines/upload" element={
            <FlagGate flag="outline-iq" fallback={<Navigate to="/outlines/library" replace />}>
              <Suspense fallback={<PageLoader message="Loading outline upload..." />}>
                <NavbarProvider><UploadOutlinePage /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/outlines/analysis/:id" element={
            <FlagGate flag="outline-iq" fallback={<Navigate to="/outlines/library" replace />}>
              <Suspense fallback={<PageLoader message="Loading outline analysis..." />}>
                <NavbarProvider><OutlineAnalysisDetail /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/outlines/archive" element={
            <FlagGate flag="outlines" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading outline archive..." />}>
                <NavbarProvider><OutlineArchive /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/cases" element={
            <FlagGate flag="case-brief-library" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading cases..." />}><CasesSearchPage /></Suspense>
            </FlagGate>
          } />
          <Route path="/cases/:id" element={
            <FlagGate flag="case-brief-library" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading case details..." />}><CaseDetailPage /></Suspense>
            </FlagGate>
          } />
          <Route path="/cases/collections/*" element={
            <FlagGate flag="case-brief-library" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading case collections..." />}><CaseBriefPage /></Suspense>
            </FlagGate>
          } />
          <Route path="/courses/*" element={
            <FlagGate flag="courses" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading courses..." />}>
                <NavbarProvider><CoursesPage /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/notes" element={
            <FlagGate flag="notes" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading notes..." />}><NotesPage /></Suspense>
            </FlagGate>
          } />
          <Route path="/practice" element={
            <Suspense fallback={<PageLoader message="Loading practice..." />}>
              <NavbarProvider><PracticePage /></NavbarProvider>
            </Suspense>
          } />
          <Route path="/issue-spotter/*" element={
            <FlagGate flag="issue-spotter" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading issue spotter..." />}>
                <NavbarProvider><IssueSpotterPageWrapper /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/essay-coach/*" element={
            <FlagGate flag="essay-coach" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading essay coach..." />}>
                <NavbarProvider><EssayCoachPageWrapper /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          {/* NextGen sales — same component as admin; StudicataGuardedRoute (admin OR
              partner) is the gate. Backend omits recentPurchases (PII) for partners. */}
          <Route path="/admin/nextgen-sales" element={
            <Suspense fallback={<PageLoader message="Loading NextGen sales..." />}>
              <NextGenSalesPage />
            </Suspense>
          } />
          </Route>
          <Route path="*" element={<Navigate to="/admin/nextgen-sales" replace />} />
        </Routes>
      </>
    );
  }

  // Admin domain routes — wrapped in PersistentLayout for shared sidebar
  if (isAdmin) {
    return (
      <>
        <ScrollToTop />
        <Routes>
        <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />

        <Route path="/auth" element={<AsyncAuthPage />} />
        {allowSetupAdmin && (
          <Route path="setup-admin" element={<SetAdminSetup />} />
        )}

        {/* All admin + user-facing routes wrapped in PersistentLayout */}
        <Route element={
          <AdminGuardedRoute>
            <Suspense fallback={<PageLoader message="Loading..." />}>
              <PersistentLayout />
            </Suspense>
          </AdminGuardedRoute>
        }>
          {/* Admin-specific pages */}
          <Route path="/admin/dashboard" element={<Suspense fallback={<PageLoader message="Loading admin dashboard..." />}><AdminDashboard /></Suspense>} />
          {/* NextGen sales — PII (user emails) gated to is_admin=true via AdminOnlyRoute */}
          <Route path="/admin/nextgen-sales" element={<AdminOnlyRoute><Suspense fallback={<PageLoader message="Loading NextGen sales..." />}><NextGenSalesPage /></Suspense></AdminOnlyRoute>} />
          <Route path="/admin/users" element={<AdminOnlyRoute><Suspense fallback={<PageLoader message="Loading user management..." />}><AdminUsers /></Suspense></AdminOnlyRoute>} />
          {/* User 360 CRM detail — PII gated to is_admin=true via AdminOnlyRoute */}
          <Route path="/admin/users/:id" element={<AdminOnlyRoute><Suspense fallback={<PageLoader message="Loading user detail..." />}><AdminUserDetailPage /></Suspense></AdminOnlyRoute>} />
          <Route path="/admin/analytics" element={<AdminOnlyRoute><Suspense fallback={<PageLoader message="Loading web analytics..." />}><AdminWebAnalytics /></Suspense></AdminOnlyRoute>} />
          <Route path="/admin/error-logs" element={<AdminOnlyRoute><Suspense fallback={<PageLoader message="Loading error logs..." />}><AdminErrorLogs /></Suspense></AdminOnlyRoute>} />
          {/* JDS Features — consolidated content-CRUD host (Courses overview / Flashcards / Outlines / Case Law).
              Staff-accessible (gated by the wrapping AdminGuardedRoute), matching the sidebar 'staff' visibility. */}
          <Route path="/admin/jds-features" element={<Suspense fallback={<PageLoader message="Loading JDS features..." />}><JdsFeaturesPage /></Suspense>} />
          {/* Legacy list routes → redirect into the consolidated host's matching tab. */}
          <Route path="/admin/courses" element={<Navigate to="/admin/jds-features?tab=courses" replace />} />
          {/* Heavy CourseDetail stays its own standalone route — NOT a tab. */}
          <Route path="/admin/courses/:courseId" element={<Suspense fallback={<PageLoader message="Loading course details..." />}><LazyAdminCourseDetail /></Suspense>} />
          <Route path="/admin/flashcards" element={<Navigate to="/admin/jds-features?tab=flashcards" replace />} />
          <Route path="/admin/outlines" element={<Navigate to="/admin/jds-features?tab=outlines" replace />} />
          <Route path="/admin/cases" element={<Navigate to="/admin/jds-features?tab=cases" replace />} />
          <Route path="/admin/blog" element={<Suspense fallback={<PageLoader message="Loading blog management..." />}><AdminBlogManagement /></Suspense>} />
          <Route path="/admin/blog/generate" element={<Suspense fallback={<PageLoader message="Loading AI generator..." />}><BlogAIGenerator /></Suspense>} />
          <Route path="/admin/settings" element={<AdminOnlyRoute><Suspense fallback={<PageLoader message="Loading admin settings..." />}><AdminSettings /></Suspense></AdminOnlyRoute>} />
          <Route path="/admin/content-health" element={<AdminOnlyRoute><Suspense fallback={<PageLoader message="Loading content health..." />}><AdminContentHealth /></Suspense></AdminOnlyRoute>} />
          <Route path="/admin/moderation" element={<Suspense fallback={<PageLoader message="Loading community moderation..." />}><AdminCommunityModeration /></Suspense>} />
          <Route path="/admin/support" element={<Suspense fallback={<PageLoader message="Loading support queue..." />}><AdminSupport /></Suspense>} />
          <Route path="/admin/support/:id" element={<Suspense fallback={<PageLoader message="Loading support thread..." />}><AdminSupport /></Suspense>} />
          <Route path="/admin/set-admin" element={<AdminOnlyRoute><Suspense fallback={<PageLoader message="Loading admin setup..." />}><SetAdminStatus /></Suspense></AdminOnlyRoute>} />

          {/* User-facing pages accessible from admin sidebar */}
          <Route path="/chat/:threadId?" element={
            <FlagGate flag="ask-jds" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading chat..." />}><ChatContainer /></Suspense>
            </FlagGate>
          } />
          <Route path="/notes" element={
            <FlagGate flag="notes" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading notes..." />}><NotesPage /></Suspense>
            </FlagGate>
          } />
          <Route path="/dashboard/*" element={
            <Suspense fallback={<PageLoader message="Loading dashboard..." />}>
              <NavbarProvider><IntelligenceDashboard /></NavbarProvider>
            </Suspense>
          } />
          <Route path="/settings/*" element={<SettingsPage />} />
          <Route path="/flashcards/*" element={
            <FlagGate flag="flashcards" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading flashcards..." />}>
                <NavbarProvider><FlashcardsPage /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/issue-spotter/*" element={
            <FlagGate flag="issue-spotter" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading issue spotter..." />}>
                <NavbarProvider><IssueSpotterPageWrapper /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/essay-coach/*" element={
            <FlagGate flag="essay-coach" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading essay coach..." />}>
                <NavbarProvider><EssayCoachPageWrapper /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/outlines" element={<Navigate to="/outlines/library" replace />} />
          <Route path="/outlines/library" element={
            <FlagGate flag="outlines" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading outline library..." />}>
                <NavbarProvider><OutlineLibrary /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/outlines/analyses" element={
            <FlagGate flag="outline-iq" fallback={<Navigate to="/outlines/library" replace />}>
              <Suspense fallback={<PageLoader message="Loading analyses..." />}>
                <NavbarProvider><OutlineAnalyses /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/outlines/upload" element={
            <FlagGate flag="outline-iq" fallback={<Navigate to="/outlines/library" replace />}>
              <Suspense fallback={<PageLoader message="Loading outline upload..." />}>
                <NavbarProvider><UploadOutlinePage /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/outlines/analysis/:id" element={
            <FlagGate flag="outline-iq" fallback={<Navigate to="/outlines/library" replace />}>
              <Suspense fallback={<PageLoader message="Loading outline analysis..." />}>
                <NavbarProvider><OutlineAnalysisDetail /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/cases" element={
            <FlagGate flag="case-brief-library" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading cases..." />}><CasesSearchPage /></Suspense>
            </FlagGate>
          } />
          <Route path="/cases/:id" element={
            <FlagGate flag="case-brief-library" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading case details..." />}><CaseDetailPage /></Suspense>
            </FlagGate>
          } />
          <Route path="/courses/*" element={
            <FlagGate flag="courses" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading courses..." />}>
                <NavbarProvider><CoursesPage /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/community/*" element={
            <Suspense fallback={<PageLoader message="Loading community..." />}>
              <NavbarProvider><CommunityPage /></NavbarProvider>
            </Suspense>
          } />

          {/* NextGen routes — accessible from admin domain for testing */}
          <Route path="/nextgen" element={<Navigate to="/nextgen/skills/practice" replace />} />
          <Route path="/nextgen/dashboard" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenDashboardPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/coursework" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenCourseworkLibrary /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/coursework/:courseId" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenCourseworkPlayer /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/coursework/:courseId/:moduleId" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenCourseworkPlayer /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/skills" element={<Navigate to="/nextgen/skills/practice" replace />} />
          <Route path="/nextgen/skills/practice" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenSkillsPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/skills/drills" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenSkillsPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/skills/library" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenSkillsPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/skills/mcq" element={<Navigate to="/nextgen/practice/mcq?formats=mcq,mcq_b" replace />} />
          <Route path="/nextgen/skills/iqs" element={<Navigate to="/nextgen/practice/iqs" replace />} />
          <Route path="/nextgen/skills/pt" element={<Navigate to="/nextgen/practice/pt" replace />} />
          <Route path="/nextgen/skills/:taskId" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenTaskDetail /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/simulations" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenSimulationsPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/sim/:sectionNumber" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenSectionSimPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/sim/:sessionId/review" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenSimReviewPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/exam/:runId" element={
            <Suspense fallback={<PageLoader />}><NextGenFullExamPage /></Suspense>
          } />
          <Route path="/nextgen/exam/:runId/results" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenExamResultsPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/progress" element={<Navigate to="/nextgen/progress/overview" replace />} />
          <Route path="/nextgen/progress/overview" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenProgressPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/progress/readiness" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenProgressPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/progress/insights" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenProgressPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/practice" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenMCQPracticePage /></NextGenLayout></Suspense>
          } />
          {/* Old workspace routes retired — redirect to practice system */}
          <Route path="/nextgen/workbench/:taskId" element={<Navigate to="/nextgen/practice/pt" replace />} />
          <Route path="/nextgen/client-chat/:taskId" element={<Navigate to="/nextgen/practice/pt" replace />} />
          <Route path="/nextgen/grade/:taskId" element={<Navigate to="/nextgen/practice/pt" replace />} />
          <Route path="/nextgen/coach" element={
            <Suspense fallback={<PageLoader />}><NextGenCoachPage /></Suspense>
          } />
        </Route>

        {/* Catch-all 404 route - must be last */}
        <Route path="*" element={
          <Suspense fallback={<PageLoader message="Loading..." />}>
            <NotFoundPage />
          </Suspense>
        } />
      </Routes>
      </>
    );
  }
  
  // Regular routes for Ask JDS and JD Simplified domains
  return (
    <>
      <ScrollToTop />
      <Routes>
      <Route path="/" element={<HomePage />} />
      
      {/* Add back the login redirect without triggering auth initialization */}
      <Route path="/login" element={<SimpleRedirect to="/auth" />} />
      
      <Route path="/auth" element={
        <AsyncAuthPage />
      } />
      
      {/* Auth callback route for email confirmation */}
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Resend confirmation email route */}
      <Route path="/auth/resend-confirmation" element={<ResendConfirmationPage />} />

      {/* Password reset routes */}
      <Route path="/auth/reset-password" element={
        <Suspense fallback={<PageLoader message="Loading..." />}>
          <ForgotPasswordPage />
        </Suspense>
      } />
      <Route path="/auth/update-password" element={
        <Suspense fallback={<PageLoader message="Loading..." />}>
          <ResetPasswordPage />
        </Suspense>
      } />

      {/* Legal Pages - Public Access (Lazy-loaded) */}
      <Route path="/terms" element={<Suspense fallback={<PageLoader message="Loading terms..." />}><TermsOfService /></Suspense>} />
      <Route path="/privacy" element={<Suspense fallback={<PageLoader message="Loading privacy policy..." />}><PrivacyPolicy /></Suspense>} />
      <Route path="/disclaimer" element={<Suspense fallback={<PageLoader message="Loading disclaimer..." />}><Disclaimer /></Suspense>} />
      
      {/* Feature Landing Pages - Public Access */}
      <Route path="/features/outlines" element={
        <Suspense fallback={<PageLoader message="Loading outlines..." />}>
          <OutlinesFeaturePage />
        </Suspense>
      } />

      {/* Subject Preview Pages - Public SEO pages */}
      <Route path="/outlines/:slug" element={
        <Suspense fallback={<PageLoader message="Loading outline..." />}>
          <SubjectPreviewPage />
        </Suspense>
      } />

      {/* Debug Routes - Development Only */}
      {import.meta.env.DEV && (
        <Route 
          path="/debug/usermaven" 
          element={
            <Suspense fallback={<PageLoader message="Loading debug..." />}>
              <UsermavenDebug />
            </Suspense>
          } 
        />
      )}
      
      {/* Pricing Page — Pure Client v1: no purchase UI in the native app.
          Native users are redirected home; web is unchanged. */}
      <Route path="/pricing" element={
        <ProtectedRoute>
          <NativePurchaseGuard>
            <AsyncPricingPage />
          </NativePurchaseGuard>
        </ProtectedRoute>
      } />
      
      {/* /unlimited deprecated 2026-05-11 — redirect to canonical /pricing.
          Catches any cached browser tabs / external bookmarks. */}
      <Route path="/unlimited" element={<Navigate to="/pricing" replace />} />
      
      {/* Checkout Confirmation Page (Standalone). Post-purchase result screen —
          native users have no web-checkout entry point, so a deep-link here is
          redirected home rather than showing checkout-adjacent chrome. */}
      <Route path="/checkout-confirmation" element={
        <NativePurchaseGuard>
          <AsyncCheckoutConfirmationPage />
        </NativePurchaseGuard>
      } />

      {/* Cancellation feedback survey (Standalone, PUBLIC — token in ?token= is
          the credential). NO auth guard, NO PersistentLayout — a churning user
          arrives from an email likely logged out. Board ODItTB29ohrpwAgU2gUf. */}
      <Route path="/cancel-feedback" element={
        <Suspense fallback={<PageLoader message="Loading..." />}>
          <CancelFeedbackPage />
        </Suspense>
      } />

      {/* Study Group Invite Landing — public route (works for unauth users).
          Flag-gated by community-board + community-study-groups. */}
      <Route path="/community/groups/join/:code" element={
        <Suspense fallback={<PageLoader message="Loading invite..." />}>
          <FlagGatedGroupInvite />
        </Suspense>
      } />

      {/* Community marketing landing — public, no auth, no flag gate.
          Pain-point-first page targeting law students studying alone. */}
      <Route path="/study-together" element={
        <Suspense fallback={<PageLoader message="Loading..." />}>
          <StudyTogetherPage />
        </Suspense>
      } />

      {/* NextGen public routes — accessible without login (marketing/upgrade pages).
          Available on dev (askjds) and live (jdsimplified) — soft-launch enables
          direct-URL access on prod even though sidebar nav is role-gated.
          F.10: route elements wrapped in `<FlagGate flag="nextgen-access">`.
          Domain hard rule `(isAskJDS || isJDSimplified)` retained — flag is
          layered ON TOP. */}
      {(isAskJDS || isJDSimplified) && (
        <>
          <Route path="/nextgen" element={
            <FlagGate flag="nextgen-access" fallback={<Navigate to="/" replace />} pending={<PageLoader message="Loading NextGen Prep..." />}>
              <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                <NextGenLandingPage />
              </Suspense>
            </FlagGate>
          } />
          <Route path="/nextgen/product" element={<Navigate to="/nextgen-prep" replace />} />
          <Route path="/nextgen/pricing" element={
            <ProtectedRoute>
              <FlagGate flag="nextgen-access" fallback={<Navigate to="/" replace />}>
                <NativePurchaseGuard>
                  <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                    <NextGenUpgradePage />
                  </Suspense>
                </NativePurchaseGuard>
              </FlagGate>
            </ProtectedRoute>
          } />
          {/* Backward compat — old /nextgen/upgrade path redirects to canonical /nextgen/pricing.
              Existing emails, ad creatives, and Stripe success URLs may still reference /upgrade. */}
          <Route path="/nextgen/upgrade" element={<Navigate to="/nextgen/pricing" replace />} />
          <Route path="/nextgen-prep" element={
            <FlagGate flag="nextgen-access" fallback={<Navigate to="/" replace />} pending={<PageLoader message="Loading NextGen Prep..." />}>
              <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                <NextGenPrepPage />
              </Suspense>
            </FlagGate>
          } />
          {/* Dev-only layout preview for founder review (unlinked, noindex). Same
              flag gate as /nextgen-prep. Do not promote to main until approved. */}
          <Route path="/nextgen-prep-preview" element={
            <FlagGate flag="nextgen-access" fallback={<Navigate to="/" replace />} pending={<PageLoader message="Loading NextGen Prep..." />}>
              <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                <NextGenPrepPreviewPage />
              </Suspense>
            </FlagGate>
          } />
        </>
      )}

      {/* Protected routes wrapped in PersistentLayout */}
      <Route element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader message="Loading..." />}>
            <PersistentLayout />
          </Suspense>
        </ProtectedRoute>
      }>
        <Route path="/chat/:threadId?" element={
          <FlagGate flag="ask-jds" fallback={<Navigate to="/" replace />}>
            <Suspense fallback={<PageLoader message="Loading chat..." />}><ChatContainer /></Suspense>
          </FlagGate>
        } />
        <Route path="/settings/*" element={<SettingsPage />} />
        <Route path="/dashboard/*" element={
          <Suspense fallback={<PageLoader message="Loading dashboard..." />}>
            <NavbarProvider>
              <IntelligenceDashboard />
            </NavbarProvider>
          </Suspense>
        } />
        {/* Courses dashboard (original Dashboard component) */}
        <Route path="/courses-dashboard" element={
          <Suspense fallback={<PageLoader message="Loading courses..." />}>
            <NavbarProvider>
              <JDSDashboard />
            </NavbarProvider>
          </Suspense>
        } />
        <Route path="/roadmap" element={
          <Suspense fallback={<PageLoader message="Loading roadmap..." />}>
            <Roadmap />
          </Suspense>
        } />
        {/* Diagnostic Assessment */}
        <Route path="/diagnostic" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader message="Loading diagnostic..." />}>
              <NavbarProvider>
                <DiagnosticPage />
              </NavbarProvider>
            </Suspense>
          </ProtectedRoute>
        } />
        {/* Syllabus Upload - Redirect to new dashboard location */}
        <Route path="/syllabus" element={<Navigate to="/dashboard/classes" replace />} />
        {/* Study Plan */}
        <Route path="/study-plan" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader message="Loading study plan..." />}>
              <NavbarProvider>
                <StudyPlanPage />
              </NavbarProvider>
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/study-plan/create" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader message="Loading study plan..." />}>
              <NavbarProvider>
                <StudyPlanPage />
              </NavbarProvider>
            </Suspense>
          </ProtectedRoute>
        } />
        {/* Full plan detail now lives inside the dashboard shell — redirect the
            legacy standalone route to /dashboard/plan/:planId (Workbench IA move). */}
        <Route path="/study-plan/:planId" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader message="Loading study plan..." />}>
              <StudyPlanRedirect />
            </Suspense>
          </ProtectedRoute>
        } />
        {/* Import Scores */}
        <Route path="/import-scores" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader message="Loading score import..." />}>
              <NavbarProvider>
                <ImportScoresPage />
              </NavbarProvider>
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/flashcards/*" element={
          <FlagGate flag="flashcards" fallback={<Navigate to="/dashboard" replace />}>
            <Suspense fallback={<PageLoader message="Loading flashcards..." />}>
              <NavbarProvider>
                <FlashcardsPage />
              </NavbarProvider>
            </Suspense>
          </FlagGate>
        } />

        {/* Practice Landing Page (gateway to Issue Spotter + Essay Coach) */}
        <Route path="/practice" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader message="Loading practice..." />}>
              <NavbarProvider>
                <PracticePage />
              </NavbarProvider>
            </Suspense>
          </ProtectedRoute>
        } />

        {/* Issue Spotter Routes */}
        <Route path="/issue-spotter/*" element={
          <ProtectedRoute>
            <FlagGate flag="issue-spotter" fallback={<Navigate to="/dashboard" replace />}>
              <Suspense fallback={<PageLoader message="Loading issue spotter..." />}>
                <NavbarProvider>
                  <IssueSpotterPageWrapper />
                </NavbarProvider>
              </Suspense>
            </FlagGate>
          </ProtectedRoute>
        } />

        {/* Essay Coach Routes */}
        <Route path="/essay-coach/*" element={
          <ProtectedRoute>
            <FlagGate flag="essay-coach" fallback={<Navigate to="/dashboard" replace />}>
              <Suspense fallback={<PageLoader message="Loading essay coach..." />}>
                <NavbarProvider>
                  <EssayCoachPageWrapper />
                </NavbarProvider>
              </Suspense>
            </FlagGate>
          </ProtectedRoute>
        } />

        {/* Quiz Routes - SHELVED: Not working well, may be restored later */}
        {/* <Route path="/quiz/setup" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader message="Loading quiz setup..." />}>
              <NavbarProvider>
                <QuizSetup />
              </NavbarProvider>
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/quiz" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader message="Loading quiz..." />}>
              <NavbarProvider>
                <QuizMode />
              </NavbarProvider>
            </Suspense>
          </ProtectedRoute>
        } /> */}
        <Route path="/courses/*" element={
          <FlagGate flag="courses" fallback={<Navigate to="/dashboard" replace />}>
            <Suspense fallback={<PageLoader message="Loading courses..." />}>
              <NavbarProvider>
                <CoursesPage />
              </NavbarProvider>
            </Suspense>
          </FlagGate>
        } />
        <Route path="/course-detail/:id" element={
          <Suspense fallback={<PageLoader message="Loading course details..." />}>
            <PublicCourseDetail />
          </Suspense>
        } />
        <Route path="/subscription/success" element={
          <Suspense fallback={<PageLoader message="Loading subscription details..." />}>
            <SubscriptionSuccess />
          </Suspense>
        } />
        {/* Outline routes - library is the default */}
        <Route path="/outlines" element={
          <Navigate to="/outlines/library" replace />
        } />
        <Route path="/outlines/library" element={
          <FlagGate flag="outlines" fallback={<Navigate to="/dashboard" replace />}>
            <Suspense fallback={<PageLoader message="Loading outline library..." />}>
              <NavbarProvider>
                <OutlineLibrary />
              </NavbarProvider>
            </Suspense>
          </FlagGate>
        } />
        <Route path="/outlines/analyses" element={
          <FlagGate flag="outline-iq" fallback={<Navigate to="/outlines/library" replace />}>
            <Suspense fallback={<PageLoader message="Loading your analyses..." />}>
              <NavbarProvider>
                <OutlineAnalyses />
              </NavbarProvider>
            </Suspense>
          </FlagGate>
        } />
        <Route path="/outlines/upload" element={
          <FlagGate flag="outline-iq" fallback={<Navigate to="/outlines/library" replace />}>
            <Suspense fallback={<PageLoader message="Loading outline upload..." />}>
              <NavbarProvider>
                <UploadOutlinePage />
              </NavbarProvider>
            </Suspense>
          </FlagGate>
        } />
        <Route path="/outlines/analysis/:id" element={
          <FlagGate flag="outline-iq" fallback={<Navigate to="/outlines/library" replace />}>
            <Suspense fallback={<PageLoader message="Loading outline analysis..." />}>
              <NavbarProvider>
                <OutlineAnalysisDetail />
              </NavbarProvider>
            </Suspense>
          </FlagGate>
        } />
        <Route path="/outlines/archive" element={
          <FlagGate flag="outlines" fallback={<Navigate to="/dashboard" replace />}>
            <Suspense fallback={<PageLoader message="Loading outline archive..." />}>
              <NavbarProvider>
                <OutlineArchive />
              </NavbarProvider>
            </Suspense>
          </FlagGate>
        } />

        {/* NextGen Prep Routes — available on dev + live (prod soft-launch).
            Sidebar nav is role-gated (admin/partner only on live), but routes
            themselves are open by direct URL. Entitlement still gates feature
            use via NextGenContentGate (soft-paywall: deep routes redirect
            unentitled users to /nextgen/pricing; directory/listing surfaces
            stay browseable). */}
        {/* Public routes (/nextgen, /nextgen/product, /nextgen/pricing) are defined ABOVE the ProtectedRoute wrapper */}
        {(isAskJDS || isJDSimplified) && (
          <>
            {/* Redirects from old URLs — no entitlement check needed */}
            <Route path="/nextgen/videos" element={<Navigate to="/nextgen/coursework" replace />} />
            <Route path="/nextgen/outlines" element={<Navigate to="/nextgen/coursework" replace />} />
            <Route path="/nextgen/intelligence" element={<Navigate to="/nextgen/coach" replace />} />
            <Route path="/nextgen/practice" element={<Navigate to="/nextgen/skills/practice" replace />} />
            <Route path="/nextgen/practice/:taskId" element={<PracticeTaskRedirect />} />
            <Route path="/nextgen/coursework/outlines" element={<Navigate to="/nextgen/coursework" replace />} />

            {/* Cycle activation + purchase confirmation — auth required, NOT entitlement-gated */}
            <Route path="/nextgen/activate" element={
              <Suspense fallback={<PageLoader message="Loading..." />}>
                <NextGenLayout><NextGenActivatePage /></NextGenLayout>
              </Suspense>
            } />
            <Route path="/nextgen/purchase-confirmed" element={
              <FlagGate flag="nextgen-access" fallback={<Navigate to="/" replace />}>
                {/* Post-purchase result screen — unreachable via a native checkout;
                    redirect native users home rather than show a retry-to-pricing. */}
                <NativePurchaseGuard>
                  <Suspense fallback={<PageLoader message="Loading..." />}>
                    <NextGenPurchaseConfirmedPage />
                  </Suspense>
                </NativePurchaseGuard>
              </FlagGate>
            } />

            {/* Preview route — auth required for signal attribution, NOT entitlement-gated */}
            <Route path="/nextgen/preview" element={
              <FlagGate flag="nextgen-access" fallback={<Navigate to="/" replace />}>
                <Suspense fallback={<PageLoader message="Loading preview..." />}>
                  <NextGenPreviewPage />
                </Suspense>
              </FlagGate>
            } />

            {/* Public preview — Library uses nextgen_tasks_public view + counts RPC.
                Designed for unentitled users to browse the catalog before converting. */}
            <Route path="/nextgen/skills/library" element={
              <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                <NextGenLayout>
                  <NextGenSkillsPage />
                </NextGenLayout>
              </Suspense>
            } />
            {/* Legacy redirect */}
            <Route path="/nextgen/mcq-practice" element={<Navigate to="/nextgen/practice/mcq" replace />} />

            {/* Browseable surfaces — directory/listing pages stay public so
                unentitled users can see what's inside before converting.
                These pages may render empty states or limited data when the
                user has no entitlement, but they don't 406 or time out.
                The actual content (taking questions, watching lessons,
                opening task details) is gated below. */}
            <Route path="/nextgen/coursework" element={
              <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                <NextGenLayout>
                  <NextGenCourseworkLibrary />
                </NextGenLayout>
              </Suspense>
            } />
            <Route path="/nextgen/skills" element={<Navigate to="/nextgen/skills/practice" replace />} />
            <Route path="/nextgen/skills/practice" element={
              <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                <NextGenLayout>
                  <NextGenSkillsPage />
                </NextGenLayout>
              </Suspense>
            } />
            <Route path="/nextgen/skills/drills" element={
              <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                <NextGenLayout>
                  <NextGenSkillsPage />
                </NextGenLayout>
              </Suspense>
            } />
            <Route path="/nextgen/skills/mcq" element={<Navigate to="/nextgen/practice/mcq?formats=mcq,mcq_b" replace />} />
            <Route path="/nextgen/skills/iqs" element={<Navigate to="/nextgen/practice/iqs" replace />} />
            <Route path="/nextgen/skills/pt" element={<Navigate to="/nextgen/practice/pt" replace />} />
            <Route path="/nextgen/progress" element={<Navigate to="/nextgen/progress/overview" replace />} />
            <Route path="/nextgen/progress/overview" element={
              <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                <NextGenLayout>
                  <NextGenProgressPage />
                </NextGenLayout>
              </Suspense>
            } />
            <Route path="/nextgen/progress/readiness" element={
              <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                <NextGenLayout>
                  <NextGenProgressPage />
                </NextGenLayout>
              </Suspense>
            } />
            <Route path="/nextgen/progress/insights" element={
              <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                <NextGenLayout>
                  <NextGenProgressPage />
                </NextGenLayout>
              </Suspense>
            } />
            <Route path="/nextgen/simulations" element={
              <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                <NextGenLayout>
                  <NextGenSimulationsPage />
                </NextGenLayout>
              </Suspense>
            } />
            <Route path="/nextgen/exam-map" element={
              <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                <NextGenLayout>
                  <NextGenExamMapPage />
                </NextGenLayout>
              </Suspense>
            } />
            <Route path="/nextgen/dashboard" element={
              <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                <NextGenLayout>
                  <NextGenDashboardPage />
                </NextGenLayout>
              </Suspense>
            } />
            {/* Coach — browseable. Unentitled users see the UI + greeting +
                suggestion chips but the input is replaced with a trial CTA
                (CoachPreviewNotice). Sending messages is gated in-page. */}
            <Route path="/nextgen/coach" element={
              <Suspense fallback={<PageLoader message="Loading Study Coach..." />}>
                <NextGenLayout>
                  <NextGenCoachPage />
                </NextGenLayout>
              </Suspense>
            } />

            {/* ============================================================== */}
            {/* UBE / MBE standalone bar-prep product — board chxw5cJrcarpNLY2HrDg */}
            {/* UbeLayout self-gates on the `ube-bar-prep` flag (FlagGate →       */}
            {/* Navigate to "/" when OFF). Sidebar entry-point additionally       */}
            {/* requires ube_package entitlement + active_products[] includes     */}
            {/* 'ube'. Tabs mirror NextGen (Skills / Simulations / Progress /     */}
            {/* Coach) per founder decision. Progress is fully built (subject     */}
            {/* axis); the other three land on an honest "coming soon" placeholder */}
            {/* until their UBE content/data pipeline lands (other windows' work). */}
            {/* ============================================================== */}
            {/* Browse-mode paywall (founder direction, board 87q2TNhDml6gO9d2BTp8):
                  OUTER gate = UbeLayout's <FlagGate flag="ube-bar-prep"> — flag OFF
                    → Navigate to "/" (whole section + nav disappear); flag ON →
                    section visible to EVERYONE (entitled and unentitled alike).
                  NO inner hard gate on these browse surfaces. The four functional
                    pages (Skills / Simulations / Progress / Coach) render for all
                    flag-on users; unentitled users see their analytics dashboard
                    with a <UbeBrowseModeCallout> nudge instead of a redirect.
                Mirrors NextGen exactly: NextGenContentGate (hard redirect) wraps
                ONLY act-on-content leaf surfaces (take a question, run a sim, deep
                coach) — never browse/analytics surfaces. UbeContentGate is kept in
                the codebase, reserved for those future Legacy interaction leaves. */}
            <Route path="/ube" element={<Navigate to="/ube/progress" replace />} />
            <Route path="/ube/skills" element={<Navigate to="/ube/skills/practice" replace />} />
            {/* PUBLIC pricing page — INSIDE UbeLayout's flag gate (so it only exists
                when ube-bar-prep is ON) but OUTSIDE UbeContentGate (so unentitled
                users can reach it to purchase). Auth-required for checkout +
                signal attribution. Uses its own PricingTopBar chrome, not the
                UbeLayout nav (matches /nextgen/pricing, which uses PricingTopBar
                rather than NextGenLayout). */}
            <Route path="/ube/pricing" element={
              <ProtectedRoute>
                <FlagGate flag="ube-bar-prep" fallback={<Navigate to="/" replace />} pending={<PageLoader message="Loading Legacy Bar Prep..." />}>
                  <NativePurchaseGuard>
                    <Suspense fallback={<PageLoader message="Loading Legacy Bar Prep..." />}>
                      <UbePricingPage />
                    </Suspense>
                  </NativePurchaseGuard>
                </FlagGate>
              </ProtectedRoute>
            } />
            <Route path="/ube/skills/practice" element={
              <Suspense fallback={<PageLoader message="Loading Legacy Bar Prep..." />}>
                <UbeLayout><UbeComingSoonPage /></UbeLayout>
              </Suspense>
            } />
            <Route path="/ube/simulations" element={
              <Suspense fallback={<PageLoader message="Loading Legacy Bar Prep..." />}>
                <UbeLayout><UbeComingSoonPage /></UbeLayout>
              </Suspense>
            } />
            <Route path="/ube/progress" element={
              <Suspense fallback={<PageLoader message="Loading Legacy Bar Prep..." />}>
                <UbeLayout><UbeProgressPage /></UbeLayout>
              </Suspense>
            } />
            <Route path="/ube/coach" element={
              <Suspense fallback={<PageLoader message="Loading Legacy Bar Prep..." />}>
                <UbeLayout><UbeCoachPage /></UbeLayout>
              </Suspense>
            } />

            {/* Entitlement-gated content — interaction/leaf surfaces only.
                Soft-paywall: NextGenContentGate redirects none/expired to
                /nextgen/pricing (the canonical sales/trial-activation page).
                These are the surfaces where the user *acts on* content:
                taking a question, opening a task detail, watching a lesson,
                running a simulation, chatting with the coach. */}
            <Route element={
              <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                <NextGenContentGate><Outlet /></NextGenContentGate>
              </Suspense>
            }>
              {/* Task detail — opening a specific task (original 406 source) */}
              <Route path="/nextgen/skills/:taskId" element={
                <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                  <NextGenLayout>
                    <NextGenTaskDetail />
                  </NextGenLayout>
                </Suspense>
              } />
              {/* MCQ practice — taking questions */}
              <Route path="/nextgen/practice/mcq" element={
                <Suspense fallback={<PageLoader message="Loading practice..." />}>
                  <NextGenLayout><MCQPracticeWithReset /></NextGenLayout>
                </Suspense>
              } />
              <Route path="/nextgen/practice/mcq/:questionId" element={
                <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenMCQSinglePage /></NextGenLayout></Suspense>
              } />
              {/* Coursework player — watching a specific lesson */}
              <Route path="/nextgen/coursework/:courseId" element={
                <NextGenLayout>
                  <NextGenCourseworkPlayer />
                </NextGenLayout>
              } />
              <Route path="/nextgen/coursework/:courseId/:moduleId" element={
                <NextGenLayout>
                  <NextGenCourseworkPlayer />
                </NextGenLayout>
              } />
              <Route path="/nextgen/coursework/:courseId/:moduleId/:lessonId" element={
                <NextGenLayout>
                  <NextGenCourseworkPlayer />
                </NextGenLayout>
              } />
              {/* Simulations — taking the sim + reviewing it */}
              <Route path="/nextgen/sim/:sectionNumber" element={
                <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenSectionSimPage /></NextGenLayout></Suspense>
              } />
              <Route path="/nextgen/sim/:sessionId/review" element={
                <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                  <NextGenLayout>
                    <NextGenSimReviewPage />
                  </NextGenLayout>
                </Suspense>
              } />
              <Route path="/nextgen/exam/:runId" element={
                <FlagGate flag="nextgen-access" fallback={<Navigate to="/" replace />}>
                  <Suspense fallback={<PageLoader />}><NextGenFullExamPage /></Suspense>
                </FlagGate>
              } />
              <Route path="/nextgen/exam/:runId/results" element={
                <FlagGate flag="nextgen-access" fallback={<Navigate to="/" replace />}>
                  <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenExamResultsPage /></NextGenLayout></Suspense>
                </FlagGate>
              } />
              <Route path="/nextgen/sets/:setId" element={
                <FlagGate flag="nextgen-access" fallback={<Navigate to="/" replace />}>
                  <Suspense fallback={<PageLoader />}><NextGenSetRedirect /></Suspense>
                </FlagGate>
              } />
              {/* IQS / PT / Drafting practice — taking questions */}
              <Route path="/nextgen/practice/iqs" element={
                <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                  <NextGenLayout><NextGenIQSPracticePage /></NextGenLayout>
                </Suspense>
              } />
              <Route path="/nextgen/practice/iqs/:setId" element={
                <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                  <NextGenLayout><NextGenIQSPracticePage /></NextGenLayout>
                </Suspense>
              } />
              <Route path="/nextgen/practice/pt" element={
                <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                  <NextGenLayout><NextGenPTPracticePage /></NextGenLayout>
                </Suspense>
              } />
              <Route path="/nextgen/practice/pt/:setId" element={
                <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                  <NextGenLayout><NextGenPTPracticePage /></NextGenLayout>
                </Suspense>
              } />
              <Route path="/nextgen/practice/drafting" element={
                <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                  <NextGenLayout><NextGenDraftingPracticePage /></NextGenLayout>
                </Suspense>
              } />
              <Route path="/nextgen/practice/drafting/:setId" element={
                <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                  <NextGenLayout><NextGenDraftingPracticePage /></NextGenLayout>
                </Suspense>
              } />
              {/* /nextgen/practice/counseling/:taskId route retired 2026-05-25 (board
                  RYu91USgyqQZ3W7gj4qi) — client_chat task type has zero rows. */}
              <Route path="/nextgen/practice/counseling/:taskId" element={<Navigate to="/nextgen/practice/pt" replace />} />
              {/* Workspace routes retired — redirect to practice system */}
              <Route path="/nextgen/workspace/:taskId" element={<Navigate to="/nextgen/practice/pt" replace />} />
              <Route path="/nextgen/workspace/:taskId/chat" element={<Navigate to="/nextgen/practice/pt" replace />} />
              <Route path="/nextgen/workspace/:taskId/results" element={<Navigate to="/nextgen/practice/pt" replace />} />
            </Route>
          </>
        )}

        {/* Case Collections Routes - Using FlashcardsPage structure */}
        {/* Cases System — unified under /cases/* — F.15 flag-gated by `case-brief-library` */}
        <Route path="/cases" element={
          <FlagGate flag="case-brief-library" fallback={<Navigate to="/dashboard" replace />}>
            <Suspense fallback={<PageLoader message="Loading cases..." />}>
              <CasesSearchPage />
            </Suspense>
          </FlagGate>
        } />
        <Route path="/cases/:id" element={
          <FlagGate flag="case-brief-library" fallback={<Navigate to="/dashboard" replace />}>
            <Suspense fallback={<PageLoader message="Loading case details..." />}>
              <CaseDetailPage />
            </Suspense>
          </FlagGate>
        } />
        <Route path="/cases/collections/*" element={
          <FlagGate flag="case-brief-library" fallback={<Navigate to="/dashboard" replace />}>
            <Suspense fallback={<PageLoader message="Loading case collections..." />}>
              <CaseBriefPage />
            </Suspense>
          </FlagGate>
        } />

        {/* Redirects for legacy case law and collection URLs */}
        <Route path="/case-law" element={<Navigate to="/cases" replace />} />
        <Route path="/case-law/*" element={<Navigate to="/cases" replace />} />
        <Route path="/case-collections" element={<Navigate to="/cases/collections" replace />} />
        <Route path="/case-collections/*" element={<Navigate to="/cases/collections" replace />} />
        
        {/* Question Bank Routes - DEPRECATED: Migrated to flashcards table */}
        {/* See docs/study-mode/QUESTION-BANK-CONSOLIDATION-PLAN.md */}
        {/* Redirect old Question Bank URLs to Unified Study */}
        <Route path="/questions/*" element={<Navigate to="/flashcards/unified-study" replace />} />

        
        {/* Notes Routes — F.15 flag-gated by `notes` */}
        <Route path="/notes" element={
          <FlagGate flag="notes" fallback={<Navigate to="/dashboard" replace />}>
            <Suspense fallback={<PageLoader message="Loading notes..." />}>
              <NotesPage />
            </Suspense>
          </FlagGate>
        } />

        {/* Community Routes */}
        <Route path="/community/*" element={
          <Suspense fallback={<PageLoader message="Loading community..." />}>
            <NavbarProvider>
              <CommunityPage />
            </NavbarProvider>
          </Suspense>
        } />
      </Route>
      
      {/* Legacy course URLs — redirect to /courses/:courseId/... */}
      <Route path="/course/:courseId" element={<CourseRedirect />} />
      <Route path="/course/:courseId/module/:moduleId" element={<CourseRedirect />} />
      <Route path="/course/:courseId/module/:moduleId/lesson/:lessonId" element={<CourseRedirect />} />

      {/* Catch-all 404 route - must be last */}
      <Route path="*" element={
        <Suspense fallback={<PageLoader message="Loading..." />}>
          <NotFoundPage />
        </Suspense>
      } />
    </Routes>
    </>
  );
}

// Page loader component for suspense fallbacks
function PageLoader({ message = "Loading..." }: { message?: string }) {
  return (
    <div className="flex flex-col justify-center items-center h-screen bg-white dark:bg-gray-900">
      <LoadingSpinner className="h-12 w-12 mb-4" />
      <p className="text-muted-foreground dark:text-gray-400">{message}</p>
    </div>
  );
}

/**
 * UTM Tracking Component
 * Must be rendered inside BrowserRouter for useLocation access.
 * Captures UTM parameters from URL and stores them for attribution.
 */
function UTMTrackingCapture() {
  useUTMTracking({
    trackArrival: true,
    debug: import.meta.env.DEV,
  });
  return null;
}

/**
 * Deep Link Initializer
 * Must be rendered inside BrowserRouter for useNavigate access.
 * Wires the native Capacitor deep-link handler (Universal Links, App Links,
 * custom scheme, Firebase auth callbacks) to React Router navigation.
 *
 * No-op on web: initializeDeepLinks() self-guards on isNativeApp and returns
 * early on non-native platforms, so this renders nothing and does nothing in
 * the browser build.
 */
function DeepLinkInitializer() {
  const navigate = useNavigate();
  useEffect(() => {
    initializeDeepLinks(navigate);
  }, [navigate]);
  return null;
}

// Wrapper function for the entire app
function AppWrapper() {
  // Read pinned state from localStorage synchronously to avoid flash:
  // unpinned users should start with sidebar collapsed, not expanded.
  const [isPinned, setIsPinned] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('sidebar-is-pinned');
      return stored ? JSON.parse(stored) === true : false;
    } catch { return false; }
  });
  const [isExpanded, setIsExpanded] = useState(isPinned);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  
  // Initialize analytics tracking
  useAnalytics();

  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      // Auto-collapse sidebar on mobile
      if (window.innerWidth < 768) {
        setIsExpanded(false);
      }
    };
    
    // Initial check
    checkMobile();
    
    // Add listener for window resize
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  useEffect(() => {
    // Clean up potential auth-related localStorage items that might cause issues
    const keysToReset = [
      'protected_redirect_attempts',
      'auth_redirect_attempts'
    ];

    keysToReset.forEach(key => {
      try {
        if (sessionStorage.getItem(key)) {
          logger.debug(`Cleaning up ${key} from sessionStorage`);
          sessionStorage.removeItem(key);
        }
      } catch (e) {
        logger.warn(`Error cleaning up ${key}:`, e);
      }
    });

    // Remove prerendered SEO content from DOM now that React has mounted.
    // #seo-content is hidden by default via CSS — this removes it entirely.
    document.getElementById('seo-content')?.remove();
  }, []);
  
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system" storageKey="ui-theme">
        <DomainProvider>
          <SubscriptionProvider>
            <LearningProfileProvider>
              <PaywallProvider>
              <CloseProvider>
                <SidebarContext value={{ isExpanded, setIsExpanded, isMobile, isPinned, setIsPinned }}>
                  <SelectedThreadContext value={{ selectedThreadId, setSelectedThreadId }}>
                    <ErrorBoundary>
                      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                        <TrialBannerProvider>
                          <UTMTrackingCapture />
                          <DeepLinkInitializer />
                          <div className="h-screen flex flex-col">
                            <GlobalTrialBanner />
                            <div className="flex-1 min-h-0 flex flex-col">
                              <AppRoutes />
                            </div>
                          </div>
                          <OnboardingModalHost />
                          <Toaster />
                          <HotToaster position="top-right" />
                          <OfflineIndicator />
                          <UpdateBanner />
                          <LayoutDebugger />
                        </TrialBannerProvider>
                      </BrowserRouter>
                    </ErrorBoundary>
                  </SelectedThreadContext>
                </SidebarContext>
              </CloseProvider>
              </PaywallProvider>
            </LearningProfileProvider>
          </SubscriptionProvider>
        </DomainProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default AppWrapper;// Test CI/CD deployment - Wed Aug 20 09:32:56 -05 2025
